import React, { useRef, useEffect } from 'react';
import { Box } from '@mui/material';
import { ChevronDown, ChevronRight, Car, MapPin, CheckCircle2, AlertTriangle, XCircle, CheckCircle } from 'lucide-react';
import { Vehicle, Booking, AvailabilityWindow, ZoomLevel, ViewMode, VehicleType, MaintenancePeriod } from './types';
import { themeColors } from './themeColors';
import { ContinuousTimelineHeader } from './ContinuousTimelineHeader';
// import { VehicleRow } from './VehicleRow';
// import { GroupedVehicleRow } from './GroupedVehicleRow';
import { AvailabilityOverlay } from './AvailabilityOverlay';
import { BookingBlock } from './BookingBlock';
import { MaintenanceBlock } from './MaintenanceBlock';
import { calculateVehicleFeasibility } from './feasibilityEngine';
import { groupVehiclesByType, calculateTimeSegments, calculateAvailabilitySummary } from './aggregationUtils';
import { getVehicleTypeColor } from './vehicleColors';

interface SchedulerGridProps {
  vehicles: Vehicle[];
  bookings: Booking[];
  maintenancePeriods: MaintenancePeriod[];
  selectedDate: Date;
  zoomLevel: ZoomLevel;
  availabilityWindow: AvailabilityWindow | null;
  selectedVehicle: Vehicle | null;
  onVehicleSelect: (vehicle: Vehicle) => void;
  scrollToTime?: Date;
}

const ROW_HEIGHT = 60;
const VEHICLE_COLUMN_WIDTH = 256;

export function SchedulerGrid({
  vehicles,
  bookings,
  maintenancePeriods,
  selectedDate,
  availabilityWindow,
  selectedVehicle,
  onVehicleSelect,
  zoomLevel,
  scrollToTime
}: SchedulerGridProps) {
  const timelineScrollRef = useRef<HTMLDivElement>(null);
  const vehicleScrollRef = useRef<HTMLDivElement>(null);
  const headerScrollRef = useRef<HTMLDivElement>(null);
  const [currentTime] = React.useState(new Date());
  

  // Sync horizontal scroll between header and timeline body
  const handleTimelineScroll = (e: React.UIEvent<HTMLDivElement>) => {
    if (headerScrollRef.current) {
      headerScrollRef.current.scrollLeft = e.currentTarget.scrollLeft;
    }
    // Sync vertical scroll with vehicle column
    if (vehicleScrollRef.current) {
      vehicleScrollRef.current.scrollTop = e.currentTarget.scrollTop;
    }
  };
  
  // Sync vertical scroll from vehicle column to timeline
  const handleVehicleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    if (timelineScrollRef.current) {
      timelineScrollRef.current.scrollTop = e.currentTarget.scrollTop;
    }
  };
  
  // Calculate hour width based on zoom level
  const getHourWidth = () => {
    switch (zoomLevel) {
      // case '1h': return 120;
      case '2h': return 60;
      case '4h': return 30;
      case '8h': return 20;
      case '1d': return 15;
      default: return 60;
    }
  };
  
  // const hourWidth = getHourWidth();
  
  // // Continuous timeline: show 21 days (504 hours) for extended visibility
  // const totalDays = 21;
  // const totalHours = totalDays * 24;
  // const totalWidth = totalHours * hourWidth;
  
  // // Start date is 2 days before selected date for context
  // const startDate = new Date(selectedDate);
  // startDate.setDate(startDate.getDate() - 2);
  // startDate.setHours(0, 0, 0, 0);
  const hourWidth = getHourWidth();

// Dynamically calculate timeline range based on bookings
const calculateTimelineRange = () => {
  // Start date is 2 days before selected date for context
  const start = new Date(selectedDate);
  start.setDate(start.getDate() - 2);
  start.setHours(0, 0, 0, 0);
  
  // Find the latest booking end time
  let latestBookingEnd = new Date(start);
  latestBookingEnd.setDate(latestBookingEnd.getDate() + 21); // Default 21 days
  
  if (bookings.length > 0) {
    const maxBookingEnd = Math.max(...bookings.map(b => b.endTime.getTime()));
    const bookingEndDate = new Date(maxBookingEnd);
    
    // Extend timeline to cover the latest booking + 2 days buffer
    if (bookingEndDate > latestBookingEnd) {
      latestBookingEnd = new Date(bookingEndDate);
      latestBookingEnd.setDate(latestBookingEnd.getDate() + 2);
    }
  }
  
  // Calculate total hours between start and end
  const totalHours = Math.ceil((latestBookingEnd.getTime() - start.getTime()) / (1000 * 60 * 60));
  const totalDays = Math.ceil(totalHours / 24);
  
  return { start, totalHours, totalDays };
};

const { start: startDate, totalHours, totalDays } = calculateTimelineRange();
const totalWidth = totalHours * hourWidth;
  
  // Group vehicles by type
  const vehicleGroups = groupVehiclesByType(vehicles);
  // const vehicleTypes: VehicleType[] = ['Sedan', 'SUV-CNG', 'SUV-Diesel', 'Premium SUV'];
  const vehicleTypes: VehicleType[] = ['Maruti Suzuki Dzire', 'Toyota Rumion S', 'Kia Carens Premium', 'Toyota Crysta GX 7 STR'];
  
  // Create flattened list of vehicles grouped by type (but no collapse/expand)
  const sortedVehicles: Vehicle[] = vehicleTypes.flatMap(type => 
    vehicleGroups.get(type) || []
  );
  
  // Calculate feasibility for each vehicle
  const vehicleFeasibilities = availabilityWindow
    ? vehicles.map(vehicle => 
        calculateVehicleFeasibility(
          vehicle,
          bookings.filter(b => b.vehicleId === vehicle.id),
          maintenancePeriods.filter(m => m.vehicleId === vehicle.id),
          availabilityWindow
        )
      )
    : [];
  
  // Scroll to specific time when scrollToTime changes
  useEffect(() => {
    if (timelineScrollRef.current && scrollToTime) {
      const hoursSinceStart = (scrollToTime.getTime() - startDate.getTime()) / (1000 * 60 * 60);
      const scrollPosition = Math.max(0, (hoursSinceStart - 2) * hourWidth);
      timelineScrollRef.current.scrollLeft = scrollPosition;
    }
  }, [scrollToTime, startDate, hourWidth]);
  
  return (
    <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', bgcolor: themeColors.background }}>
      {/* Header row */}
      <Box sx={{ display: 'flex', borderBottom: `1px solid ${themeColors.border}`, bgcolor: themeColors.background }}>
        {/* Fixed vehicle header */}
        <Box 
          sx={{
            flexShrink: 0,
            borderRight: `1px solid ${themeColors.border}`,
            bgcolor: themeColors.muted,
            px: 2,
            py: 1.5,
            width: VEHICLE_COLUMN_WIDTH
          }}
        >
          <Box sx={{ fontWeight: themeColors.fontWeightMedium, fontSize: '0.875rem', color: themeColors.foreground }}>Vehicles</Box>
        </Box>
        
        {/* Scrollable timeline header */}
        <Box sx={{ flex: 1, overflow: 'hidden' }}>
          <Box ref={headerScrollRef} sx={{ overflowX: 'hidden' }}>
            <ContinuousTimelineHeader
              startDate={startDate}
              totalHours={totalHours}
              hourWidth={hourWidth}
              currentTime={currentTime}
              availabilityWindow={availabilityWindow}
            />
          </Box>
        </Box>
      </Box>
      
      {/* Body: split into fixed vehicle column and scrollable timeline */}
      <Box sx={{ flex: 1, display: 'flex', overflow: 'hidden' }}>
        {/* Fixed left column: Vehicle labels */}
        <Box 
          ref={vehicleScrollRef}
          sx={{
            flexShrink: 0,
            borderRight: `1px solid ${themeColors.border}`,
            bgcolor: themeColors.background,
            overflowY: 'auto',
            width: VEHICLE_COLUMN_WIDTH
          }}
          onScroll={handleVehicleScroll}
        >
          {sortedVehicles.map((vehicle) => {
            const vehicleIndex = vehicles.findIndex(v => v.id === vehicle.id);
            const feasibility = vehicleFeasibilities[vehicleIndex];
            
            return (
              <VehicleLabel
                key={vehicle.id}
                vehicle={vehicle}
                feasibility={feasibility}
                isSelected={selectedVehicle?.id === vehicle.id}
                onSelect={() => onVehicleSelect(vehicle)}
                rowHeight={ROW_HEIGHT}
                hasAvailabilityWindow={availabilityWindow !== null}
                availabilityWindow={availabilityWindow}
              />
            );
          })}
        </Box>
        
        {/* Scrollable right column: Timeline grid */}
        <Box 
          ref={timelineScrollRef}
          sx={{ flex: 1, overflow: 'auto', position: 'relative' }}
          onScroll={handleTimelineScroll}
        >
          <Box sx={{ position: 'relative', width: totalWidth, minHeight: '100%' }}>
            {/* Day alternating background stripes */}
            <Box sx={{ position: 'absolute', inset: 0, pointerEvents: 'none' }}>
              {Array.from({ length: totalDays }).map((_, dayIndex) => {
                const currentDay = new Date(startDate);
                currentDay.setDate(startDate.getDate() + dayIndex);
                const hoursSinceStart = dayIndex * 24;
                const isEvenDay = dayIndex % 2 === 0;
                
                return (
                  <Box
                    key={`day-bg-${dayIndex}`}
                    sx={{
                      position: 'absolute',
                      top: 0,
                      bottom: 0,
                      left: hoursSinceStart * hourWidth,
                      width: 24 * hourWidth,
                      bgcolor: isEvenDay ? themeColors.dayStripeEven : themeColors.dayStripeOdd
                    }}
                  />
                );
              })}
            </Box>
            
            {/* Grid lines - must align with header */}
            <Box sx={{ position: 'absolute', inset: 0, pointerEvents: 'none' }}>
              {Array.from({ length: totalHours + 1 }).map((_, i) => {
                const currentHour = new Date(startDate);
                currentHour.setHours(startDate.getHours() + i);
                const isDayBoundary = currentHour.getHours() === 0;
                
                return (
                  <Box
                    key={i}
                    sx={{
                      position: 'absolute',
                      top: 0,
                      bottom: 0,
                      left: i * hourWidth,
                      borderLeft: isDayBoundary ? `2px solid ${themeColors.borderStrong}` : `1px solid ${themeColors.border}`
                    }}
                  />
                );
              })}
            </Box>
            
            {/* Current time indicator */}
            {(() => {
              const hoursSinceStart = (currentTime.getTime() - startDate.getTime()) / (1000 * 60 * 60);
              
              if (hoursSinceStart >= 0 && hoursSinceStart <= totalHours) {
                const currentPosition = hoursSinceStart * hourWidth;
                
                return (
                  <Box
                    sx={{
                      position: 'absolute',
                      top: 0,
                      bottom: 0,
                      left: currentPosition,
                      width: '2px',
                      bgcolor: themeColors.destructive,
                      pointerEvents: 'none',
                      zIndex: 20
                    }}
                  >
                    <Box sx={{
                      position: 'absolute',
                      top: -4,
                      left: -6,
                      width: 12,
                      height: 12,
                      bgcolor: themeColors.destructive,
                      borderRadius: '50%'
                    }} />
                    <Box sx={{
                      position: 'absolute',
                      top: 8,
                      left: 8,
                      bgcolor: themeColors.destructive,
                      color: themeColors.destructiveForeground,
                      fontSize: '0.75rem',
                      px: 1,
                      py: 0.25,
                      borderRadius: 1,
                      whiteSpace: 'nowrap'
                    }}>
                      Now
                    </Box>
                  </Box>
                );
              }
              return null;
            })()}
            
            {/* Availability window overlay */}
            {availabilityWindow && (
              <AvailabilityOverlay
                availabilityWindow={availabilityWindow}
                selectedDate={selectedDate}
                hourWidth={hourWidth}
                totalRows={sortedVehicles.length}
                rowHeight={ROW_HEIGHT}
                startDate={startDate}
              />
            )}
            
            {/* Timeline rows */}
            <Box sx={{ position: 'relative' }}>
              {sortedVehicles.map((vehicle) => {
                const vehicleBookings = bookings.filter(b => b.vehicleId === vehicle.id);
                const vehicleMaintenancePeriods = maintenancePeriods.filter(m => m.vehicleId === vehicle.id);
                
                return (
                  <VehicleTimelineRow
                    key={vehicle.id}
                    vehicle={vehicle}
                    bookings={vehicleBookings}
                    maintenancePeriods={vehicleMaintenancePeriods}
                    hourWidth={hourWidth}
                    rowHeight={ROW_HEIGHT}
                    startDate={startDate}
                  />
                );
              })}
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
}

// Separate components for labels and timeline rows

interface VehicleGroupLabelProps {
  vehicleType: VehicleType;
  totalCount: number;
  availableCount: number;
  warningCount: number;
  isExpanded: boolean;
  onToggle: () => void;
  rowHeight: number;
  hasAvailabilityWindow: boolean;
}

function VehicleGroupLabel({
  vehicleType,
  totalCount,
  availableCount,
  warningCount,
  isExpanded,
  onToggle,
  rowHeight,
  hasAvailabilityWindow
}: VehicleGroupLabelProps) {
  const colors = getVehicleTypeColor(vehicleType);
  
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        borderBottom: `1px solid ${themeColors.border}`,
        px: 1.5,
        py: 1.25,
        cursor: 'pointer',
        transition: 'colors 0.2s',
        height: rowHeight,
        ...colors.row
      }}
      onClick={onToggle}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
        <Box sx={{ flexShrink: 0 }}>
          {isExpanded ? (
            <ChevronDown style={{ width: 16, height: 16, color: '#525252' }} />
          ) : (
            <ChevronRight style={{ width: 16, height: 16, color: '#525252' }} />
          )}
        </Box>
        
        <Box sx={{ borderRadius: 1, p: 0.75, ...colors.iconBg }}>
          <Car style={{ width: 16, height: 16, ...colors.icon }} />
        </Box>
        
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box sx={{ width: 12, height: 12, borderRadius: 1, border: 1, ...colors.primary, ...colors.border }} />
            <Box component="span" sx={{ fontWeight: themeColors.fontWeightMedium, fontSize: '0.875rem', color: themeColors.foreground }}>
              {vehicleType}
            </Box>
          </Box>
          <Box sx={{ fontSize: '0.75rem', color: themeColors.mutedForeground }}>
            {totalCount} {totalCount === 1 ? 'vehicle' : 'vehicles'}
            {hasAvailabilityWindow && availableCount > 0 && (
              <Box component="span" sx={{ ml: 1, color: themeColors.success, fontWeight: themeColors.fontWeightMedium }}>
                • {availableCount} available
              </Box>
            )}
            {hasAvailabilityWindow && warningCount > 0 && (
              <Box component="span" sx={{ ml: 1, color: themeColors.warning, fontWeight: themeColors.fontWeightMedium }}>
                • {warningCount} with warnings
              </Box>
            )}
          </Box>
        </Box>
      </Box>
    </Box>
  );
}

interface VehicleLabelProps {
  vehicle: Vehicle;
  feasibility?: any;
  isSelected: boolean;
  onSelect: () => void;
  rowHeight: number;
  hasAvailabilityWindow: boolean;
  availabilityWindow: AvailabilityWindow | null;
}

function VehicleLabel({
  vehicle,
  feasibility,
  isSelected,
  onSelect,
  rowHeight,
  hasAvailabilityWindow,
  availabilityWindow
}: VehicleLabelProps) {
  
  // Check if vehicle type matches the requested type
  // If no specific type requested, all types match
  const requestedType = availabilityWindow?.tripIntent.vehicleTypeRequired;
// const requestedType = availabilityWindow?.tripIntent.vehicleTypeRequired;
const isTypeMatch = hasAvailabilityWindow && requestedType 
  ? vehicle.vehicleType === requestedType 
  : true;  
  const colors = getVehicleTypeColor(vehicle.vehicleType);
  
  const getFeasibilityIcon = () => {
    if (!feasibility || !hasAvailabilityWindow) return null;
    
    // Unavailable vehicles get red cross
    if (feasibility.status === 'unavailable') {
      return <XCircle style={{ width: 20, height: 20, color: '#dc2626' }} />;
    }
    
    // Available vehicles
    if (feasibility.status === 'available') {
      if (isTypeMatch) {
        // Perfect match: green filled check
        return <CheckCircle style={{ width: 20, height: 20, color: '#16a34a' }} />;
      } else {
        // Type mismatch but available: orange/amber check
        return <CheckCircle style={{ width: 20, height: 20, color: '#f59e0b' }} />;
      }
    }
    
    // Warning status
    if (feasibility.status === 'warning') {
      if (isTypeMatch) {
        // Type match with warning: green check with alert
        return <CheckCircle2 style={{ width: 20, height: 20, color: '#16a34a' }} />;
      } else {
        // Type mismatch with warning: amber alert
        return <AlertTriangle style={{ width: 20, height: 20, color: '#f59e0b' }} />;
      }
    }
    
    return null;
  };
  
  return (
  <Box
    sx={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      borderBottom: `1px solid ${themeColors.border}`,
      px: 2,
      py: 1.5,
      cursor: 'pointer',
      transition: 'colors 0.2s',
      height: rowHeight,
      ...colors.light,
      ...(isSelected && {
        outline: `2px solid ${themeColors.selection}`,
        outlineOffset: '-2px'
      })
    }}
    onClick={onSelect}
  >
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25, flex: 1, minWidth: 0 }}>
      {/* Vehicle name */}
      <Box component="span" sx={{ fontSize: '0.875rem', fontWeight: themeColors.fontWeightMedium, color: themeColors.foreground, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
        {vehicle.vehicleName}
      </Box>
      {/* Vehicle number */}
      <Box component="span" sx={{ fontSize: '0.75rem', color: themeColors.mutedForeground }}>
        {vehicle.vehicleNumber}
      </Box>
    </Box>
    
    {/* Feasibility icon */}
    {feasibility && hasAvailabilityWindow && (
      <Box sx={{ flexShrink: 0, ml: 1 }}>
        {getFeasibilityIcon()}
      </Box>
    )}
  </Box>
  );
}

interface GroupTimelineRowProps {
  timeSegments: any[];
  vehicleType: VehicleType;
  hourWidth: number;
  rowHeight: number;
  dayStart: Date;
}

function GroupTimelineRow({
  timeSegments,
  vehicleType,
  hourWidth,
  rowHeight,
  dayStart
}: GroupTimelineRowProps) {
  const colors = getVehicleTypeColor(vehicleType);
  
  return (
    <Box 
      sx={{
        position: 'relative',
        borderBottom: `1px solid ${themeColors.border}`,
        height: rowHeight,
        ...colors.row
      }}
    >
      {timeSegments.map((segment, index) => {
        const startHours = (segment.startTime.getTime() - dayStart.getTime()) / (1000 * 60 * 60);
        const duration = (segment.endTime.getTime() - segment.startTime.getTime()) / (1000 * 60 * 60);
        
        const left = startHours * hourWidth;
        const width = duration * hourWidth;
        
        const occupancyRatio = segment.totalCount > 0 
          ? segment.occupiedCount / segment.totalCount 
          : 0;
        
        let blockColor = 'transparent';
        if (occupancyRatio > 0) {
          if (occupancyRatio >= 0.75) {
            blockColor = colors.busyHigh.bgcolor;
          } else if (occupancyRatio >= 0.4) {
            blockColor = colors.busyMedium.bgcolor;
          } else {
            blockColor = colors.busyLow.bgcolor;
          }
        }
        
        return (
          <Box
            key={index}
            sx={{
              position: 'absolute',
              top: 0,
              bottom: 0,
              left,
              width,
              bgcolor: blockColor,
              borderLeft: `1px solid ${themeColors.border}`
            }}
            title={`${segment.occupiedCount}/${segment.totalCount} busy`}
          >
            {width > 40 && occupancyRatio > 0 && (
              <Box sx={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Box component="span" sx={{ fontSize: '0.75rem', fontWeight: themeColors.fontWeightMedium, color: themeColors.foreground }}>
                  {segment.occupiedCount}/{segment.totalCount}
                </Box>
              </Box>
            )}
          </Box>
        );
      })}
    </Box>
  );
}

interface VehicleTimelineRowProps {
  vehicle: Vehicle;
  bookings: Booking[];
  maintenancePeriods: MaintenancePeriod[];
  hourWidth: number;
  rowHeight: number;
  startDate: Date;
}

function VehicleTimelineRow({
  vehicle,
  bookings,
  maintenancePeriods,
  hourWidth,
  rowHeight,
  startDate
}: VehicleTimelineRowProps) {
  
  
  const getBookingPosition = (booking: Booking) => {
    const startHours = (booking.startTime.getTime() - startDate.getTime()) / (1000 * 60 * 60);
    const duration = (booking.endTime.getTime() - booking.startTime.getTime()) / (1000 * 60 * 60);
    
    const position = {
      left: startHours * hourWidth,
      width: duration * hourWidth
    };
    
    return position;
  };
  
  return (
    <Box 
      sx={{
        position: 'relative',
        borderBottom: `1px solid ${themeColors.border}`,
        height: rowHeight
      }}
    >
      {bookings.map((booking) => {
        const position = getBookingPosition(booking);
        return (
          <BookingBlock
            key={booking.id}
            booking={booking}
            left={position.left}
            width={position.width}
            height={rowHeight - 8}
            vehicleType={vehicle.vehicleType}
          />
        );
      })}
      {maintenancePeriods.map((maintenance) => {
        // Calculate position for maintenance periods similar to bookings
        const startHours = (maintenance.startTime.getTime() - startDate.getTime()) / (1000 * 60 * 60);
        const duration = (maintenance.endTime.getTime() - maintenance.startTime.getTime()) / (1000 * 60 * 60);
        const position = {
          left: startHours * hourWidth,
          width: duration * hourWidth
        };
        return (
          <MaintenanceBlock
            key={maintenance.id}
            maintenance={maintenance}
            left={position.left}
            width={position.width}
            height={rowHeight - 8}
          />
        );
      })}
    </Box>
  );
}